import React from 'react';
import Table from '@ssg/common/Components/Table';
import SummaryTable from './SummaryTable';
import addThousandSeperator from '@ssg/common/Helpers/addThousandSeperator';
import { useTranslation } from 'react-i18next';
import { InvoiceAssortment, InvoiceLine } from './InvoiceModal';
import { useQuery } from '@apollo/client';
import { GetDebitor_debitor, GetInvoiceReasonCodes, ValidationRuleBypassInput } from '../../../GraphQL';
import { loader } from 'graphql.macro';
import { InvoiceCategory } from '@ssg/common/GraphQL';
import {
	DEFAULT_ENVFEE_NAME,
	DEFAULT_SUPPLYFEE_NAME,
	getEnvFeeOnInvoice,
	getSupplyFeeOnInvoice,
	hasMaximumEnvironmentalFeeAmountForDebitor,
	hasMaximumSupplyFeeAmountForDebitor,
	isCaseCreatedWithinSupplyFeeDateRange,
	shouldCalculateEnvironmentalFeeForDebitor,
	shouldCalculateSupplyFeeForDebitor,
} from '../../../environmentalFeeHelper';
import { addUOM, filterInvoiceLines } from './InvoiceStepView';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { DateTime } from 'luxon';

const GET_INVOICE_REASON_CODES = loader('src/GraphQL/Invoices/GetInvoiceReasonCodes.gql');

interface Props {
	caseCreated: DateTime
	category: InvoiceCategory;
	lines: InvoiceLine[];
	reasonCodes: ValidationRuleBypassInput[];
	desciption: string;
	debitor: GetDebitor_debitor;
	assortments: InvoiceAssortment[];
	screeningLines: SelectOption[];
	accumulatedCaseSupplyFee: number;
}

const InvoiceStepSummary: React.FC<Props> = ({ caseCreated, category, lines, reasonCodes, desciption, debitor, assortments, screeningLines, accumulatedCaseSupplyFee }) => {
	const { t } = useTranslation();
	const supplyFeeFlag = useFlag(FeatureFlagEnums.SUPPLY_FEE);

	const shouldCalculateEnvFeeForDebitor = shouldCalculateEnvironmentalFeeForDebitor(debitor.environmentFeePercentage);
	const hasMaxEnvFeeAmount = hasMaximumEnvironmentalFeeAmountForDebitor(debitor.maxEnvironmentFeeAmount);
	const debitorWithSupply = debitor as GetDebitor_debitor & {
		supplyFeePercentage?: number | null;
		maxSupplyFeeAmount?: number | null;
		supplyFeeDateFrom?: string | null;
		supplyFeeDateTo?: string | null;
	};
	const shouldCalculateSupplyFeeForDebitorOnCase = shouldCalculateSupplyFeeForDebitor(debitorWithSupply.supplyFeePercentage);
	const isCaseWithinSupplyFeeDateRange = isCaseCreatedWithinSupplyFeeDateRange(caseCreated, debitorWithSupply);
	const hasMaxSupplyFeeAmount = hasMaximumSupplyFeeAmountForDebitor(debitorWithSupply.maxSupplyFeeAmount);
	// This should be cached from the previous page
	const { data: invoiceReasonCodesData, loading: invoiceReasonCodesLoading } = useQuery<GetInvoiceReasonCodes>(GET_INVOICE_REASON_CODES, {
		fetchPolicy: 'cache-first',
	});

	const subtotal = lines
		.filter(l => l.no !== DEFAULT_ENVFEE_NAME && l.no !== DEFAULT_SUPPLYFEE_NAME)
		.reduce((price, line) => price + (line.lineAmount ?? 0), 0);
	const totalEnvFee = getEnvFeeOnInvoice(lines, debitor);
	const totalSupplyFee = getSupplyFeeOnInvoice(lines, debitorWithSupply, accumulatedCaseSupplyFee);
	const totalPrice = subtotal + totalEnvFee + (supplyFeeFlag && isCaseWithinSupplyFeeDateRange && shouldCalculateSupplyFeeForDebitorOnCase ? totalSupplyFee : 0);

	return (
		<>
			<div>
				<p className="mb-2 font-semibold">{t('case.invoice.reviewInvoice')}</p>
				{desciption.length > 0 && (
					<div className="text-blue text-sm">
						<h2 className="font-semibold">{t('common.description')}</h2>
						<p className="overflow-y-auto" style={{ maxHeight: '10rem' }}>
							{desciption}
						</p>
					</div>
				)}
				<div>
					<SummaryTable lines={addUOM(filterInvoiceLines(lines), assortments, screeningLines)} isScreening={category === InvoiceCategory.SCREENING} />
				</div>
				{shouldCalculateEnvFeeForDebitor && (
					<div className="mt-2 text-right">
						<p>
							{t('case.invoice.envFee')}: {debitor.environmentFeePercentage}% | {t('case.invoice.maxEnvFee')}: {hasMaxEnvFeeAmount ? debitor.maxEnvironmentFeeAmount : 'N/A'}
						</p>
						<p>
							{t('case.invoice.envFeeTotal')}: {addThousandSeperator(totalEnvFee)}
						</p>
					</div>
				)}
				{supplyFeeFlag && isCaseWithinSupplyFeeDateRange && shouldCalculateSupplyFeeForDebitorOnCase && (
					<div className="mt-2 text-right">
						<p>
							{t('case.invoice.supplyFee')}: {debitorWithSupply.supplyFeePercentage ?? 0}% | {t('case.invoice.maxSupplyFee')}: {hasMaxSupplyFeeAmount ? debitorWithSupply.maxSupplyFeeAmount : 'N/A'}
						</p>
						<p>
							{t('case.invoice.supplyFeeTotal')}: {addThousandSeperator(totalSupplyFee)}
						</p>
					</div>
				)}
				<p className="my-2 text-right">
					{t('case.invoice.line.total')} {addThousandSeperator(totalPrice)}
				</p>
			</div>

			{reasonCodes.length > 0 && !invoiceReasonCodesLoading && (
				<div className="border-t-1 mt-6 border-gray-200 pt-6">
					<p className="mb-2 font-semibold">{t('case.invoice.reviewBypassReasons')}</p>

					<div style={{ maxHeight: '70vh' }}>
						<Table
							data={reasonCodes}
							className="max-h-max"
							keySelector={rc => rc.rule}
							columns={[
								{
									label: 'case.invoice.rule',
									selectFn: rc => t(`case.invoice.${rc.rule}`)?.toString() ?? '',
								},
								{
									label: 'case.invoice.reasonCode',
									selectFn: rc => invoiceReasonCodesData?.invoiceReasonCodes.find(irc => irc.id === rc.reasonCode)?.name ?? '',
								},
								{
									label: 'case.invoice.comment',
									selectFn: rc => rc.comment ?? '',
								},
							]}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default InvoiceStepSummary;
